import React from 'react';
import { styled } from '@linaria/react';

import { clamp } from '@/utils';
import VisuallyHidden from '@/components/VisuallyHidden';

const LIMITS = {
  min: -60,
  max: 60,
};

function SouvlakiPieceDraggable({
  index,
  status,
  left,
  firstBB,
  thirdBB,
  handleIntersect,
  handleClear,
}) {
  const [translateBy, setTranslateBy] = React.useState(0);
  const mouseDownPosition = React.useRef(null);

  const pieceRef = React.useRef();

  const lockedTranslateBy = React.useRef(0);

  const translateByRef = React.useRef();
  translateByRef.current = translateBy;

  const checkForIntersection = () => {
    if (!firstBB || !thirdBB || !pieceRef.current) {
      return;
    }
    const pieceBB = pieceRef.current.getBoundingClientRect();

    const isIntersectingLeft =
      pieceBB.left < firstBB.left + firstBB.width - 22;
    const isIntersectingRight =
      pieceBB.left + pieceBB.width > thirdBB.left + 12;

    const isIntersecting = isIntersectingLeft || isIntersectingRight;

    if (isIntersecting && status === 'fine') {
      handleIntersect(isIntersectingLeft ? 'left' : 'right');
    } else if (
      !isIntersecting &&
      (status === 'intersecting-left' ||
        status === 'intersecting-right')
    ) {
      handleClear();
    }
  };

  const checkForIntersectionRef = React.useRef();
  checkForIntersectionRef.current = checkForIntersection;

  React.useEffect(() => {
    checkForIntersectionRef.current();
  }, [translateBy]);

  const handleDrag = React.useCallback((ev) => {
    // This event handles both mouseMove and touchMove.
    let x;
    if (ev.touches) {
      ev.preventDefault();
      const touch = ev.touches[0];
      x = touch.clientX;
    } else {
      x = ev.clientX;
    }

    if (!mouseDownPosition.current) {
      return;
    }

    let deltaX =
      x - mouseDownPosition.current.x + lockedTranslateBy.current;

    deltaX = clamp(deltaX, LIMITS.min, LIMITS.max);

    setTranslateBy(deltaX);
  }, []);

  const handleRelease = React.useCallback(() => {
    mouseDownPosition.current = null;
    lockedTranslateBy.current = translateByRef.current;

    window.removeEventListener('mousemove', handleDrag);
  }, [handleDrag]);

  const handleStart = React.useCallback(
    (ev) => {
      // This event handles both mouseMove and touchMove.
      let x, y;
      if (ev.touches) {
        ev.preventDefault();
        const touch = ev.touches[0];
        [x, y] = [touch.clientX, touch.clientX];
      } else {
        [x, y] = [ev.clientX, ev.clientX];
      }

      mouseDownPosition.current = { x, y };

      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('touchmove', handleDrag);
      window.addEventListener('mouseup', handleRelease);
      window.addEventListener('touchend', handleRelease);
    },
    [handleDrag, handleRelease]
  );

  const reset = React.useCallback(() => {
    setTranslateBy(0);
    mouseDownPosition.current = null;
    lockedTranslateBy.current = 0;
  }, []);

  const handleKeyDown = React.useCallback(
    (ev) => {
      if (ev.code === 'Enter') {
        reset();
        return;
      }

      if (ev.code !== 'ArrowLeft' && ev.code !== 'ArrowRight') {
        return;
      }

      event.preventDefault();

      const moveBy = ev.code === 'ArrowLeft' ? -10 : 10;

      setTranslateBy((currentValue) => {
        let tentative = currentValue + moveBy;

        const newValue = clamp(tentative, LIMITS.min, LIMITS.max);

        lockedTranslateBy.current = newValue;

        return newValue;
      });
    },
    [reset]
  );

  React.useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('touchmove', handleDrag);
    };
  }, [handleDrag]);

  return (
    <>
      <Background
        src={`/images/interactive-guide-to-flexbox/souvlaki-${index}-back.png`}
        alt=""
        style={{
          left,
          transform: `translateX(${translateBy}px)`,
        }}
      />
      <GrabButton
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        onKeyDown={handleKeyDown}
        style={{ left, transform: `translateX(${translateBy}px)` }}
      >
        <Foreground
          ref={pieceRef}
          draggable={false}
          alt=""
          src={`/images/interactive-guide-to-flexbox/souvlaki-${index}-front.png`}
        />
        <VisuallyHidden>
          This button inclues a draggable image of a kebab piece which
          can be dragged horizontally, but only a tiny bit, as it runs
          into the other pieces on the kebab.
        </VisuallyHidden>
      </GrabButton>
    </>
  );
}

const Background = styled.img`
  position: absolute;
  top: 0;
  z-index: 2;
  height: 100%;
`;

const GrabButton = styled.button`
  position: absolute;
  top: 0;
  z-index: 100;
  height: 100%;
  width: max-content;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const Foreground = styled.img`
  display: block;
  height: 100%;
  user-select: none;
  touch-action: none;
`;

export default SouvlakiPieceDraggable;
